import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  input,
  output,
  AfterViewInit,
  OnDestroy,
  contentChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IFilterField } from 'src/app/shared/interfaces/IFilterField';
import { COMPARE_OPERATOR } from 'src/app/shared/enums/CompareOperator.enum';
import { IFilterOptions } from 'src/app/shared/interfaces/IFilterOptions';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { signal } from '@angular/core';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { NgTemplateOutlet } from '@angular/common';
import { ActionsHeaderFiltersDirective } from '../../directives/action-header-filters.directive';
import { ActionsHeaderContentDirective } from '../../directives/action-header-content.directive';
import { ActionsHeaderAdditionalFiltersDirective } from '../../directives/action-header-additional-filters.directive';

@Component({
  selector: 'app-actions-header',
  templateUrl: './actions-header.component.html',
  styleUrls: ['./actions-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    MatInput,
    MatSuffix,
    TranslatePipe,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    NgTemplateOutlet,
  ],
})
export class ActionsHeaderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() set hasFilterFields(hasFilterFields: boolean) {
    if (hasFilterFields) {
      this._hasFilterFields = true;
      this.hasClearFiltersBtn = true;
    }
  }

  public get hasFilterFields(): boolean {
    return this._hasFilterFields;
  }

  @Input() hasClearFiltersBtn: boolean;
  readonly hasOperators = input<boolean>();
  readonly hasSearchField = input<boolean>();
  readonly searchInputTestId = input<string | null>(null);
  readonly filterFields = input<IFilterField[]>([]);
  readonly multipleOptions = input(true);
  readonly searchOptionsChanged = output<IFilterOptions>();
  readonly clearFiltersButtonClicked = output<void>();

  public selectedFilterFields: string[];
  public selectedCompareOperator: COMPARE_OPERATOR | '' = COMPARE_OPERATOR.CONTAINS;
  public compareOperators: COMPARE_OPERATOR[] = Object.values(COMPARE_OPERATOR);
  public searchValue = '';
  @ViewChild('searchInput', { static: false }) public searchInput!: ElementRef;
  public filtersDirective = contentChild(ActionsHeaderFiltersDirective);
  public contentDirective = contentChild(ActionsHeaderContentDirective);
  public additionalFiltersDirective = contentChild(ActionsHeaderAdditionalFiltersDirective);

  private _hasFilterFields: boolean;

  protected readonly isMobile = signal(window.innerWidth <= 700);
  private resizeListener: (() => void) | undefined;

  ngOnInit(): void {
    this.mapSelectedFilterFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterFields && !changes.filterFields.firstChange) {
      this.mapSelectedFilterFields();
    }
  }

  ngAfterViewInit(): void {
    const update = () => this.isMobile.set(window.innerWidth <= 700);
    window.addEventListener('resize', update);
    this.resizeListener = () => window.removeEventListener('resize', update);
  }

  ngOnDestroy(): void {
    if (this.resizeListener) {
      this.resizeListener();
    }
  }

  private getFilterFields(): IFilterField[] {
    if (!this._hasFilterFields) {
      return [];
    }
    return this.filterFields().filter(entry => this.selectedFilterFields.includes(entry.key));
  }

  public handleInputAndSelectionChange(): void {
    const changeObject: IFilterOptions = {
      searchValue: this.searchValue,
      compareOperator: this.selectedCompareOperator,
      filterFields: this.getFilterFields(),
    };
    this.searchOptionsChanged.emit(changeObject);
  }

  public clearSearch(): void {
    this.searchValue = '';
    this.handleInputAndSelectionChange();
  }

  public clearFilters(): void {
    this.selectedFilterFields = this.filterFields()
      .filter(x => x.isSelected)
      .map(x => x.key);
    this.searchValue = '';
    this.selectedCompareOperator = COMPARE_OPERATOR.CONTAINS;
    this.clearFiltersButtonClicked.emit();
    this.handleInputAndSelectionChange();
  }

  protected updateSelectedFilterFields(selectedFilterFields: string[]): void {
    this.selectedFilterFields = selectedFilterFields;
  }

  protected updateSelectedCompareOperator(selectedCompareOperator: COMPARE_OPERATOR): void {
    this.selectedCompareOperator = selectedCompareOperator;
  }

  protected updateSearchValue(searchValue: string): void {
    this.searchValue = searchValue;
  }

  private mapSelectedFilterFields(): void {
    this.selectedFilterFields = this.filterFields()
      .filter(x => x.isSelected)
      .map(x => x.key);
  }
}
